import { useContext, useState } from 'react'
import moment from 'moment-timezone'
import { enqueueSnackbar } from 'notistack'

import { Button } from '@mui/material'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import MobileOffOutlinedIcon from '@mui/icons-material/MobileOffOutlined'
import AllInclusiveOutlinedIcon from '@mui/icons-material/AllInclusiveOutlined'

import { AppContext } from '/providers/app-context'
import { patchPass } from '/providers/passes-api'

import ExpiryDialog from '/components/passes/expiry-dialog'
import ClickToCopy from '/components/click-to-copy'

const DetailSummary = ({ template, pass, classes }) => {

  const { apiSource, apiKey, setProcessing, refreshPass, client } = useContext(AppContext)
  const now = moment.utc()

  const [calendarOpen, setCalendarOpen] = useState(false)

  if (!template || !pass) return null

  const hasExpiryDate = pass.expiry_date != null || pass.variables?.pass?.validity?.end != null
  const expiryDate = pass.expiry_date ?? pass.variables?.pass?.validity?.end

  const synchronise = async () => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Synchronising pass', variant: 'light' })
    try {
      const result = await patchPass({ apiSource, apiKey, passId: pass.pass_id, variables: {} })
      if (result?.error || result?.ok === false) {
        enqueueSnackbar({ message: 'Pass synchronisation failed', variant: 'error' })
      } else {
        enqueueSnackbar({ message: 'Pass synchronised', variant: 'success' })
        refreshPass({ templateId: template.template_id, passId: pass.pass_id })
      }
    }
    finally {
      setProcessing(false)
    }
  }

  const expireNow = async () => {
    setExpiryDate(moment().format())
  }

  const neverExpire = async () => {
    setExpiryDate(null)
  }

  const setExpiryDate = async date => {
    setProcessing(true)
    enqueueSnackbar({ message: 'Updating expiry date', variant: 'light' })
    const result = await patchPass({
      apiSource,
      apiKey,
      passId: pass.pass_id,
      variables: { "pass.validity.end": date }
    })
    if (result.error) {
      enqueueSnackbar({ message: 'Expiry date updated failed', variant: 'error' })
    } else {
      enqueueSnackbar({ message: 'Expiry date updated', variant: 'success' })
    }
    setProcessing(false)

    refreshPass({ templateId: template.template_id, passId: pass.pass_id })
  }

  const getExpiryDate = (expdate) => {
    if (!hasExpiryDate) return "no"
    if (moment.utc(now).isAfter(expiryDate)) return "expired"
    return expiryDateTimeFormat(expiryDate)
  }

  return (
    <div className={classes}>
      <div className='p-2'>
        {dataItem("Pass id", <ClickToCopy>{pass.pass_id}</ClickToCopy>)}
        <br />
        {dataItem("Created", dateTimeFormat(pass.created_date))}
        {dataItem("Last Modified", dateTimeFormat([pass.apple_modified_date, pass.google_modified_date].filter(Boolean).sort().at(-1)))}
        <br />
        {dataItem("Install status", installStatusFormat(pass.install_status))}
        {dataItem("First installed", optionalUtcDateTimeFormat(pass.first_installed_date))}
        {dataItem("Last installed", optionalUtcDateTimeFormat(pass.last_installed_date))}
        {dataItem("Last uninstalled", optionalUtcDateTimeFormat(pass.last_uninstalled_date))}
        <br />
        {dataItem("Free engagements remaining", Number(pass.free_engagements_remaining ?? 0).toLocaleString())}
        <br />
        {dataItem("Expires", getExpiryDate(expiryDate))}
        <br />
        {dataItem("Grouping id", template.grouping_id)}
        <br />
        {dataItem("Template id", <ClickToCopy>{pass.template_id}</ClickToCopy>)}
        {dataItem("Template last Modified", dateTimeFormat(template.last_modified_date))}
      </div>

      <div className='mt-5 p-2'>
        <div className='flex flex-row w-full justify-between'>
          <div className='flex flex-col gap -2 items-start'>
            <Button onClick={() => setCalendarOpen(true)}><CalendarMonthOutlinedIcon className='mr-5' />Change Expiry Date</Button>
            <Button onClick={neverExpire} disabled={pass.mode === "live" && !hasExpiryDate}><AllInclusiveOutlinedIcon className='mr-5' />Never Expire</Button>
            <Button onClick={expireNow} disabled={Boolean(expiryDate && moment.utc(expiryDate).isSameOrBefore(now))}><MobileOffOutlinedIcon className='mr-5' />Expire Immediately</Button>
          </div>
        </div>
      </div>

      <ExpiryDialog open={calendarOpen} setOpen={setCalendarOpen} expiryDate={expiryDate} setExpiryDate={setExpiryDate} />
    </div>
  )
}

export default DetailSummary

const dataItem = (title, value) => <div className='flex justify-between w-full gap-10 mb-3'><div className='text-nowrap'>{title}</div><div className='font-bold'>{value}</div></div>
const expiryDateTimeFormat = datestring => datestring ? moment.utc(datestring).tz('Europe/London').format("DD MMM YYYY HH:mm") : "no expiry"
const dateTimeFormat = datestring => datestring ? moment.utc(datestring).tz('Europe/London').format("DD MMM YYYY HH:mm") : "-"
const optionalUtcDateTimeFormat = datestring => datestring ? moment.utc(datestring).tz('Europe/London').format("DD MMM YYYY HH:mm") : "-"
const installStatusFormat = value => value === "in_use" ? "In use" : value === "dormant" ? "Dormant" : "Never used"
const yesNoFormat = booleanValue => booleanValue ? "Yes" : "No"
